/**
 * Dynamic Translation Engine — Shopify Theme
 *
 * Supports: French (fr), Spanish (es), and any future languages
 *
 * Strategy (NO MutationObserver):
 *  1. Initial full-page translate on DOMContentLoaded
 *  2. IntersectionObserver  — translates sections as they scroll into view
 *  3. shopify:section:load  — Shopify's official event for dynamically loaded sections
 *  4. Theme custom events   — cart:updated, drawer:open, quickview:open, etc.
 *  5. requestAnimationFrame periodic scan — ultra-lightweight fallback that
 *     auto-pauses in background tabs and never blocks the main thread
 *
 * Translation backend: Google Translate free endpoint (no API key required)
 * Caching: localStorage — instant on same-session page visits
 */

(function () {
  'use strict';

  /* ─── 1. Detect current locale ─────────────────────────────────── */
  var locale = (document.documentElement.lang || '').toLowerCase().split('-')[0];

  // Only run for non-English locales
  var isEnglish = !locale || locale === 'en';
  var isPreview = window.Shopify && window.Shopify.designMode;
  if (isEnglish && !isPreview) return;

  var targetLang = locale; // 'fr', 'es', etc.

  /* ─── 2. Manual overrides from theme customizer ──────────────────
     Admin enters: English text | Translated text  (one per line)
     These always win over Google Translate.                            */
  var MANUAL = {};
  try {
    var raw_overrides = (window.shopTranslationOverrides || '');
    raw_overrides.split(/\r?\n/).forEach(function (line) {
      line = line.trim();
      if (!line) return;
      var sep = line.indexOf('|');
      if (sep === -1) return;
      var en = line.substring(0, sep).trim();
      var tr = line.substring(sep + 1).trim();
      if (en && tr) MANUAL[en] = tr;
    });
  } catch (e) {}

  /* ─── 3. Translation cache (localStorage — per language) ────────── */
  var CACHE_KEY = 'dt_' + targetLang + '_v3';
  var cache = {};

  try {
    var raw = localStorage.getItem(CACHE_KEY);
    if (raw) cache = JSON.parse(raw);
  } catch (e) {}

  // If overrides changed, wipe cache so everything re-translates fresh
  var MANUAL_HASH_KEY = 'dt_' + targetLang + '_manual_hash';
  var currentHash = JSON.stringify(MANUAL);
  var prevHash = '';
  try { prevHash = localStorage.getItem(MANUAL_HASH_KEY) || ''; } catch(e) {}

  if (currentHash !== prevHash) {
    cache = {};
    try {
      localStorage.removeItem(CACHE_KEY);
      localStorage.setItem(MANUAL_HASH_KEY, currentHash);
    } catch(e) {}
  }

  // Apply current MANUAL overrides into cache
  Object.keys(MANUAL).forEach(function (k) { cache[k] = MANUAL[k]; });
  if (Object.keys(MANUAL).length) {
    try { localStorage.setItem(CACHE_KEY, JSON.stringify(cache)); } catch(e) {}
  }

  function saveCache() {
    try { localStorage.setItem(CACHE_KEY, JSON.stringify(cache)); } catch (e) {}
  }

  /* ─── 4. Show page ─────────────────────────────────────────────── */
  function showPage() {
    if (window.__showPageTimer) clearTimeout(window.__showPageTimer);
    if (window.__showPage) window.__showPage();
  }

  /* ─── 5. Helpers ───────────────────────────────────────────────── */
  // ISO codes for currencies, languages, countries — never translate these
  var SKIP_TOKENS = {
    EN:1, ES:1, FR:1, DE:1, IT:1, JA:1, NL:1, PT:1, ZH:1, AR:1, RU:1, KO:1,
    USD:1, EUR:1, GBP:1, CAD:1, AUD:1, JPY:1, CNY:1, INR:1, PKR:1, AED:1,
    US:1, CA:1, GB:1, UK:1, AU:1, NZ:1, IN:1, PK:1, JP:1, CN:1, FR_:1, DE_:1,
    PM:1, AM:1
  };

  function isTranslatable(str) {
    if (!str) return false;
    str = str.trim();
    if (str.length < 2) return false;
    // Skip pure numbers / prices / symbols
    if (/^[\d\s.,\$£€¥₨₹%+\-\/:&#@!^~|<>()\[\]{}*]+$/.test(str)) return false;
    // Skip ISO codes for languages, currencies, countries (1-3 uppercase letters)
    if (/^[A-Z]{1,3}$/.test(str) || SKIP_TOKENS[str.toUpperCase()]) return false;
    // Skip URLs
    if (/^https?:\/\//i.test(str) || /^\/\//.test(str)) return false;
    // Skip email addresses
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str)) return false;
    // Skip phone numbers (digits, spaces, dashes, parens, plus — at least 6 digits)
    if (/^[\d\s().\-+]+$/.test(str) && (str.match(/\d/g) || []).length >= 6) return false;
    // Skip strings that are 80%+ digits/symbols (covers prices like "Rs.8,600.00 PKR")
    var letters = (str.match(/[a-zA-Z]/g) || []).length;
    if (letters > 0 && letters / str.length < 0.3) return false;
    // Skip pure-currency strings like "Rs.8,600.00" or "$25.00 CAD" — money pattern
    if (/^[A-Za-z$£€¥₨₹]{0,3}\.?\s*[\d,]+(\.\d+)?(\s*[A-Z]{2,3})?$/.test(str)) return false;
    return true;
  }

  // Class/attribute-based element skip — never translate price/money/code containers
  var SKIP_ELEMENT_SELECTORS = [
    '.money', '.Price', '.Money',
    '[data-money-convertible]', '[data-product-price]',
    '.ProductMeta__Price', '.ProductItem__Price',
    '.CartDrawer__Price', '.cart__price', '.product__price',
    '.SelectButton', '.Header__LocalizationForm', '.Popover',
    '.CustomFooter__Language', '.CustomFooter__LangText', '.CustomFooter__LangLabel',
    '.shopify-payment-button',
    '.localization-form', '.localization-selector',
    '[name="locale_code"]', '[name="country_code"]'
  ];

  function shouldSkipElement(el) {
    if (!el || el.nodeType !== 1) return false;
    if (el.hasAttribute && el.hasAttribute('data-no-translate')) return true;
    for (var i = 0; i < SKIP_ELEMENT_SELECTORS.length; i++) {
      try {
        if (el.matches && el.matches(SKIP_ELEMENT_SELECTORS[i])) return true;
      } catch (e) {}
    }
    return false;
  }

  /* ─── 6. Google Translate free API ─────────────────────────────── */
  async function callGoogleTranslate(text) {
    var url =
      'https://translate.googleapis.com/translate_a/single' +
      '?client=gtx&sl=en&tl=' + targetLang + '&dt=t&q=' +
      encodeURIComponent(text);
    try {
      var resp = await fetch(url);
      if (!resp.ok) return text;
      var data = await resp.json();
      var result = '';
      if (data && data[0]) {
        data[0].forEach(function (seg) {
          if (seg && seg[0]) result += seg[0];
        });
      }
      return result || text;
    } catch (e) {
      return text;
    }
  }

  /* ─── 7. Batch translator ──────────────────────────────────────── */
  async function translateBatch(rawTexts) {
    var toFetch = [];
    var seen = {};
    rawTexts.forEach(function (t) {
      var key = (t || '').trim();
      if (key && !seen[key] && !cache[key] && isTranslatable(key)) {
        toFetch.push(key);
        seen[key] = true;
      }
    });
    if (!toFetch.length) return;

    var chunks = [];
    var chunk = [];
    var chunkLen = 0;
    toFetch.forEach(function (text) {
      if (chunk.length >= 40 || chunkLen + text.length > 3000) {
        if (chunk.length) chunks.push(chunk);
        chunk = [text];
        chunkLen = text.length;
      } else {
        chunk.push(text);
        chunkLen += text.length + 1;
      }
    });
    if (chunk.length) chunks.push(chunk);

    for (var i = 0; i < chunks.length; i++) {
      var originals = chunks[i];
      var joined    = originals.join('\n');
      var translated = await callGoogleTranslate(joined);
      var lines      = translated.split('\n');

      originals.forEach(function (orig, idx) {
        var tr = (lines[idx] || '').trim();
        if (tr && tr !== orig) cache[orig] = tr;
      });
    }

    saveCache();
  }

  /* ─── 8. DOM text-node collection ──────────────────────────────── */
  var SKIP_TAGS = {
    SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, IFRAME: 1,
    SVG: 1, PATH: 1, CIRCLE: 1, RECT: 1, POLYGON: 1, LINE: 1,
    INPUT: 1, TEXTAREA: 1, SELECT: 1, OPTION: 1,
    CODE: 1, PRE: 1, MATH: 1
  };

  function collectTextNodes(root, out) {
    out = out || [];
    if (!root) return out;

    if (root.nodeType === 3) {
      var t = root.textContent.trim();
      if (isTranslatable(t)) out.push(root);
      return out;
    }

    if (root.nodeType !== 1) return out;
    if (SKIP_TAGS[root.tagName]) return out;
    if (shouldSkipElement(root)) return out;

    for (var i = 0; i < root.childNodes.length; i++) {
      collectTextNodes(root.childNodes[i], out);
    }
    return out;
  }

  /* ─── 9. Apply translations to collected text nodes ────────────── */
  async function applyToTextNodes(nodes) {
    if (!nodes.length) return;
    var texts = nodes.map(function (n) { return n.textContent.trim(); });
    await translateBatch(texts);

    nodes.forEach(function (node) {
      var key = node.textContent.trim();
      var tr  = cache[key];
      if (tr && tr !== key) {
        var leading  = node.textContent.match(/^\s*/)[0];
        var trailing = node.textContent.match(/\s*$/)[0];
        node.textContent = leading + tr + trailing;
      }
    });
  }

  /* ─── 10. Translate element attributes ─────────────────────────── */
  var TRANSLATABLE_ATTRS = ['placeholder', 'aria-label', 'title', 'alt'];

  function elementOrAncestorSkipped(el) {
    var cur = el;
    while (cur && cur.nodeType === 1) {
      if (shouldSkipElement(cur)) return true;
      cur = cur.parentElement;
    }
    return false;
  }

  async function applyToAttributes(root) {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    var selector = TRANSLATABLE_ATTRS.map(function (a) { return '[' + a + ']'; }).join(',');
    var els = root.querySelectorAll(selector);
    if (!els.length) return;

    var texts = [];
    els.forEach(function (el) {
      if (elementOrAncestorSkipped(el)) return;
      TRANSLATABLE_ATTRS.forEach(function (attr) {
        var v = el.getAttribute(attr);
        if (v && v.trim() && isTranslatable(v.trim())) texts.push(v.trim());
      });
    });

    await translateBatch(texts);

    els.forEach(function (el) {
      if (elementOrAncestorSkipped(el)) return;
      TRANSLATABLE_ATTRS.forEach(function (attr) {
        var v = el.getAttribute(attr);
        if (v && cache[v.trim()]) el.setAttribute(attr, cache[v.trim()]);
      });
    });
  }

  /* ─── 11. Main subtree translator ──────────────────────────────── */
  var translatedSet = new WeakSet();

  var ALWAYS_RETRANSLATE_SELECTORS = [
    '.cart-drawer', '.cart-notification', '#cart-drawer',
    '.predictive-search', '.search-modal',
    '#shopify-section-cart', '[data-section-type="cart"]',
    '.mini-cart', '#mini-cart', '.MiniCart',
    '.CartDrawer', '.Drawer--cart'
  ];

  function isAlwaysRetranslate(el) {
    return ALWAYS_RETRANSLATE_SELECTORS.some(function (sel) {
      try { return el.matches(sel) || el.closest(sel); } catch (e) { return false; }
    });
  }

  async function translateSubtree(root) {
    if (!root) return;
    var alwaysRedo = isAlwaysRetranslate(root);
    if (!alwaysRedo && translatedSet.has(root)) return;
    if (!alwaysRedo) translatedSet.add(root);

    var nodes = collectTextNodes(root);
    await applyToTextNodes(nodes);
    await applyToAttributes(root);
  }

  /* ─── 12. IntersectionObserver ─────────────────────────────────── */
  var intersectionObserver = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        translateSubtree(entry.target);
        if (!isAlwaysRetranslate(entry.target)) {
          intersectionObserver.unobserve(entry.target);
        }
      }
    });
  }, { rootMargin: '300px 0px', threshold: 0 });

  function observeWithIntersection(root) {
    root = root || document;
    var els = root.querySelectorAll(
      '.shopify-section, [data-section-id], section, .card-wrapper, ' +
      '.product-card-wrapper, .blog-articles, .featured-collection, ' +
      '.cart-drawer, .cart-notification, .predictive-search, .search-modal, ' +
      '.MiniCart, .Drawer--cart, .CartDrawer'
    );
    els.forEach(function (el) { intersectionObserver.observe(el); });
  }

  /* ─── 13. Shopify section lifecycle events ─────────────────────── */
  document.addEventListener('shopify:section:load', function (event) {
    var el = event.target || (event.detail && event.detail.sectionEl) || document.body;
    translateSubtree(el);
    observeWithIntersection(el);
  });

  /* ─── 14. Shopify theme editor events ──────────────────────────── */
  ['shopify:block:select', 'shopify:section:select', 'shopify:section:reorder'].forEach(function (evt) {
    document.addEventListener(evt, function (e) {
      setTimeout(function () {
        translateSubtree(e.target || document.body);
      }, 150);
    });
  });

  /* ─── 15. Theme custom events ──────────────────────────────────── */
  var THEME_EVENTS = [
    'cart:updated', 'cart:item-added', 'cart:refresh',
    'theme:cart:open', 'ajaxcart:itemAdded', 'cartdrawer:open',
    'drawer:open', 'modal:open', 'modal:opened',
    'quickview:open', 'quickview:loaded', 'quick-add:success',
    'search:results-loaded', 'predictive-search:results',
    'recommendations:loaded', 'product:loaded', 'variant:changed',
    'pfcustom:sectionLoaded', 'gp:sectionLoaded'
  ];

  var themeEventTimers = new WeakMap();
  THEME_EVENTS.forEach(function (evtName) {
    document.addEventListener(evtName, function (e) {
      var target =
        (e.detail && (e.detail.el || e.detail.container)) ||
        e.target ||
        document.body;
      // Debounce per-target so rapid events (e.g. predictive search keystrokes)
      // collapse into a single translation pass instead of stacking
      var prev = themeEventTimers.get(target);
      if (prev) clearTimeout(prev);
      themeEventTimers.set(target, setTimeout(function () {
        themeEventTimers.delete(target);
        translateSubtree(target);
      }, 200));
    });
  });

  /* ─── 16. requestAnimationFrame periodic scan ──────────────────── */
  var lastScan    = 0;
  var SCAN_EVERY  = 3000;

  var DYNAMIC_SELECTORS = [
    '.cart-drawer', '#cart-drawer', '.cart-notification',
    '.predictive-search__results', '.search-modal__content',
    '.quickview-modal', '#quick-add-modal',
    '.recently-viewed', '#shopify-section-predictive-search',
    '.MiniCart', '.Drawer--cart', '.CartDrawer'
  ].join(',');

  function periodicScan(timestamp) {
    if (timestamp - lastScan >= SCAN_EVERY) {
      lastScan = timestamp;
      var dynamicEls = document.querySelectorAll(DYNAMIC_SELECTORS);
      dynamicEls.forEach(function (el) {
        translateSubtree(el);
      });
    }
    requestAnimationFrame(periodicScan);
  }

  /* ─── 17. Boot ─────────────────────────────────────────────────── */
  function applyCacheSync(root) {
    var nodes = collectTextNodes(root);
    nodes.forEach(function (node) {
      var key = node.textContent.trim();
      var tr  = cache[key];
      if (tr && tr !== key) {
        var leading  = node.textContent.match(/^\s*/)[0];
        var trailing = node.textContent.match(/\s*$/)[0];
        node.textContent = leading + tr + trailing;
      }
    });
    var ATTRS = ['placeholder', 'aria-label', 'title', 'alt'];
    var selector = ATTRS.map(function(a){ return '['+a+']'; }).join(',');
    root.querySelectorAll(selector).forEach(function(el) {
      if (elementOrAncestorSkipped(el)) return;
      ATTRS.forEach(function(attr) {
        var v = el.getAttribute(attr);
        if (v && cache[v.trim()]) el.setAttribute(attr, cache[v.trim()]);
      });
    });
  }

  function boot() {
    applyCacheSync(document.body);
    showPage();
    translateSubtree(document.body).then(function () {
      observeWithIntersection(document);
      requestAnimationFrame(periodicScan);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
